/**
 * Jours fériés et dates notables françaises.
 * Dates fixes + dates mobiles calculées (Pâques, Ascension, Pentecôte).
 */

export interface HolidayEntry {
  readonly name: string;
  /** true = jour férié légal (chômé), false = date notable */
  readonly official: boolean;
}

/** Algorithme de Meeus — calcule le dimanche de Pâques pour une année donnée. */
function computeEasterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function set(map: Map<string, HolidayEntry>, date: string, name: string, official: boolean) {
  map.set(date, { name, official });
}

const cache = new Map<number, Map<string, HolidayEntry>>();

export function getHolidaysForYear(year: number): Map<string, HolidayEntry> {
  const cached = cache.get(year);
  if (cached) return cached;

  const easter = computeEasterSunday(year);
  const holidays = new Map<string, HolidayEntry>();

  // Jours fériés légaux
  set(holidays, `${year}-01-01`, "Jour de l'An", true);
  set(holidays, `${year}-05-01`, "Fête du Travail", true);
  set(holidays, `${year}-05-08`, "Victoire 1945", true);
  set(holidays, `${year}-07-14`, "Fête nationale", true);
  set(holidays, `${year}-08-15`, "Assomption", true);
  set(holidays, `${year}-11-01`, "Toussaint", true);
  set(holidays, `${year}-11-11`, "Armistice 1918", true);
  set(holidays, `${year}-12-25`, "Noël", true);

  // Dates mobiles fériées (basées sur Pâques)
  set(holidays, formatDate(addDays(easter, 1)), "Lundi de Pâques", true);
  set(holidays, formatDate(addDays(easter, 39)), "Ascension", true);
  set(holidays, formatDate(addDays(easter, 50)), "Lundi de Pentecôte", true);

  // Dates notables (non fériées)
  set(holidays, `${year}-01-06`, "Épiphanie", false);
  set(holidays, `${year}-02-02`, "Chandeleur", false);
  set(holidays, `${year}-02-14`, "Saint-Valentin", false);
  set(holidays, `${year}-03-08`, "Journée des femmes", false);
  set(holidays, `${year}-04-01`, "Poisson d'avril", false);
  set(holidays, `${year}-06-21`, "Fête de la musique", false);
  set(holidays, `${year}-10-31`, "Halloween", false);
  set(holidays, `${year}-12-24`, "Réveillon de Noël", false);
  set(holidays, `${year}-12-31`, "Saint-Sylvestre", false);
  set(holidays, formatDate(addDays(easter, -47)), "Mardi Gras", false);
  set(holidays, formatDate(easter), "Pâques", false);
  set(holidays, formatDate(addDays(easter, 49)), "Pentecôte", false);

  // Fête des mères (dernier dimanche de mai, sauf si Pentecôte → 1er dimanche de juin)
  const pentecostSunday = addDays(easter, 49);
  const lastSundayMay = new Date(year, 4, 31);
  while (lastSundayMay.getDay() !== 0) lastSundayMay.setDate(lastSundayMay.getDate() - 1);
  const mothersDayDate = formatDate(lastSundayMay) === formatDate(pentecostSunday)
    ? addDays(lastSundayMay, 7)
    : lastSundayMay;
  set(holidays, formatDate(mothersDayDate), "Fête des mères", false);

  // Fête des pères (3e dimanche de juin)
  const juneFirst = new Date(year, 5, 1);
  const firstSundayJune = addDays(juneFirst, (7 - juneFirst.getDay()) % 7);
  set(holidays, formatDate(addDays(firstSundayJune, 14)), "Fête des pères", false);

  cache.set(year, holidays);
  return holidays;
}

export function getHolidayName(dateStr: string): string | null {
  const year = parseInt(dateStr.slice(0, 4), 10);
  if (isNaN(year)) return null;
  return getHolidaysForYear(year).get(dateStr)?.name ?? null;
}
